/**
 * The mayhem challenge's HUD: a slip of paper pinned top-centre while a half-brick runs — the headline,
 * a big countdown, targets got and the score — with the combo stamped beside it (×2 … ×5), a "+300" that
 * jumps off it on every hit, the stamp it has reached (TIDY / PROPER / DIABOLICAL) slammed on when it
 * gets there, and a thin red flash round the screen edge on a hit. Top-centre is free on every layout
 * (the objective clipping is top-left, cash and heat top-right, the thumbs at the bottom), and nothing
 * here takes a touch: `pointer-events: none` throughout. Presentation only; built once, updated by text
 * and class writes.
 */
import './activities.css';
import { el, setText, toggle } from '../ui/dom';

export interface MayhemHudFrame {
  title: string;
  /** Seconds left. */
  left: number;
  done: number;
  count: number;
  score: number;
  /** The combo the next target would score at (1 = none). */
  combo: number;
  /** Stamp reached so far (0..3) and its name. */
  tier: number;
  tierName: string;
  /** Score needed for the next stamp (null at the top). */
  next: number | null;
}

export class MayhemHud {
  readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly count: HTMLElement;
  private readonly score: HTMLElement;
  private readonly next: HTMLElement;
  private readonly combo: HTMLElement;
  private readonly stamp: HTMLElement;
  private readonly pop: HTMLElement;
  private readonly flash: HTMLElement;
  private lastCombo = 1;
  private lastTier = 0;
  private lastSecond = -1;

  constructor(parent: HTMLElement) {
    this.flash = el('div', 'mh-flash', undefined, parent);
    this.root = el('div', 'mh-slip bh-glass', undefined, parent);
    this.root.hidden = true;
    this.root.setAttribute('aria-live', 'polite');
    this.title = el('div', 'mh-title', undefined, this.root);
    this.clock = el('div', 'mh-clock', undefined, this.root);
    const row = el('div', 'mh-row', undefined, this.root);
    this.count = el('span', 'mh-count', undefined, row);
    this.score = el('span', 'mh-score', undefined, row);
    this.next = el('div', 'mh-next', undefined, this.root);
    this.combo = el('div', 'mh-combo bh-stamp', undefined, this.root);
    this.stamp = el('div', 'mh-tier bh-stamp', undefined, this.root);
    this.pop = el('div', 'mh-pop', undefined, this.root);
  }

  /** Null hides it. */
  set(f: MayhemHudFrame | null): void {
    if (!f) {
      this.root.hidden = true;
      this.lastCombo = 1;
      this.lastTier = 0;
      this.lastSecond = -1;
      return;
    }
    this.root.hidden = false;
    setText(this.title, f.title.toUpperCase());
    const s = Math.max(0, Math.ceil(f.left));
    setText(this.clock, `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
    toggle(this.clock, 'hurry', f.left <= 10 && f.left > 0);
    if (s !== this.lastSecond && f.left <= 10) this.beat(this.clock, 'tock');
    this.lastSecond = s;
    setText(this.count, `${f.done} of ${f.count}`);
    setText(this.score, f.score.toLocaleString('en-GB'));
    setText(this.next, f.next === null ? 'Top stamp' : `${f.next.toLocaleString('en-GB')} for ${f.tier === 0 ? 'TIDY' : f.tier === 1 ? 'PROPER' : 'DIABOLICAL'}`);
    setText(this.combo, `×${f.combo}`);
    toggle(this.combo, 'on', f.combo > 1);
    if (f.combo > this.lastCombo) this.beat(this.combo, 'beat');
    this.lastCombo = f.combo;
    setText(this.stamp, f.tierName.toUpperCase());
    toggle(this.stamp, 'on', f.tier > 0);
    if (f.tier > this.lastTier) this.beat(this.stamp, 'slam');
    this.lastTier = f.tier;
  }

  /** A target went down: "+300", and the screen edge flashes (stronger with the combo). */
  hit(points: number, combo: number): void {
    setText(this.pop, `+${points}`);
    this.beat(this.pop, 'go');
    this.flash.style.setProperty('--mh-flash', String(Math.min(0.55, 0.18 + combo * 0.07)));
    this.beat(this.flash, 'go');
  }

  private beat(e: HTMLElement, cls: string): void {
    e.classList.remove(cls);
    void e.offsetWidth;
    e.classList.add(cls);
  }

  dispose(): void {
    this.root.remove();
    this.flash.remove();
  }
}
